<template>
  <section class="financeiro-section financeiro-categorias">
    <div class="card-list">
      <article
        v-for="category in sortedCategories"
        :key="category.id"
        class="card card--with-icon"
      >
        <div
          class="card-icon-wrap"
          :class="category.kind === 'income' ? 'financeiro-cat-income' : 'financeiro-cat-expense'"
        >
          <i class="material-icons">{{ category.kind === 'income' ? 'trending_up' : 'trending_down' }}</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">{{ category.name }}</h3>
          <p class="card-meta card-secondary">
            {{ kindLabel(category.kind) }}
            <span v-if="transactionCount(category.id) > 0" class="financeiro-cat-usage">
              · {{ transactionCount(category.id) }} transação(ões)
            </span>
          </p>
        </div>
        <div class="card-actions">
          <button type="button" class="btn-icon" aria-label="Editar" @click="openEdit(category)">
            <i class="material-icons">edit</i>
          </button>
          <button type="button" class="btn-icon btn-icon-danger" aria-label="Excluir" @click="handleDeleteClick(category)">
            <i class="material-icons">delete_outline</i>
          </button>
        </div>
      </article>
    </div>

    <p v-if="sortedCategories.length === 0" class="empty-hint">
      Nenhuma categoria cadastrada. Adicione a primeira.
    </p>

    <button type="button" class="btn-fab" aria-label="Nova categoria" @click="openAdd">
      <i class="material-icons">add</i>
    </button>

    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingId ? 'Alterar categoria' : 'Nova categoria' }}</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showModal = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="form-group">
            <label for="cat-name">Nome</label>
            <input
              id="cat-name"
              v-model="categoryName"
              type="text"
              placeholder="Ex: Aluguel, Serviços, Marketing"
              :aria-invalid="!!nameError"
            />
            <p v-if="nameError" class="field-error" role="alert">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label>Tipo</label>
            <div class="financeiro-type-toggle">
              <button
                type="button"
                class="financeiro-toggle-btn"
                :class="{ 'financeiro-toggle-btn--active': categoryKind === 'income' }"
                :disabled="kindLocked"
                @click="categoryKind = 'income'"
              >
                Receita
              </button>
              <button
                type="button"
                class="financeiro-toggle-btn"
                :class="{ 'financeiro-toggle-btn--active': categoryKind === 'expense' }"
                :disabled="kindLocked"
                @click="categoryKind = 'expense'"
              >
                Despesa
              </button>
            </div>
            <p v-if="kindLocked" class="field-hint">
              Não é possível alterar o tipo: existem transações usando esta categoria.
            </p>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saveLoading" @click="showModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="saveLoading" @click="save">
          {{ saveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <Drawer :model-value="showReassignModal" @update:model-value="showReassignModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">Excluir categoria</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showReassignModal = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <p class="reassign-message">
          A categoria "{{ deleteTarget?.name }}" está em uso por
          {{ transactionCount(deleteTarget?.id) }} transação(ões).
          Escolha outra categoria do mesmo tipo para reatribuir antes de excluir.
        </p>
        <div class="form-group">
          <label for="reassign-target">Reatribuir para</label>
          <v-select
            id="reassign-target"
            :options="reassignOptions"
            :reduce="(o) => o.id"
            v-model="reassignTargetId"
            label="label"
            placeholder="Selecione a categoria"
            :clearable="false"
          />
          <p v-if="reassignError" class="field-error" role="alert">{{ reassignError }}</p>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="deleteLoading" @click="showReassignModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary btn-danger" :disabled="deleteLoading" @click="doDeleteWithReassign">
          {{ deleteLoading ? 'Excluindo...' : 'Reatribuir e excluir' }}
        </button>
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Excluir categoria"
      :message="deleteMessage"
      confirm-label="Excluir"
      :confirm-loading="deleteLoading"
      @confirm="doDeleteSimple"
    />
  </section>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed } from 'vue';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '@/firebase';
import { useToast } from '@/composables/useToast';
import { useTransactionCategoryStore } from '@/stores/transactionCategory';
import { useBankTransactionStore } from '@/stores/bankTransaction';
import { TRANSACTION_CATEGORY_KIND_LABELS } from '@/constants/transactionCategoryKinds';

export default {
  name: 'FinanceiroCategoriasTab',
  components: { Drawer, ConfirmDialog, vSelect },
  setup() {
    const { show: showToast } = useToast();
    const categoryStore = useTransactionCategoryStore();
    const bankTransactionStore = useBankTransactionStore();

    const sortedCategories = computed(() => categoryStore.allTransactionCategories);

    function transactionCount(categoryId) {
      if (!categoryId) return 0;
      return Object.values(bankTransactionStore.bankTransactions).filter(
        (tx) => tx.categoryId === categoryId
      ).length;
    }

    function kindLabel(kind) {
      return TRANSACTION_CATEGORY_KIND_LABELS[kind] || kind;
    }

    const showModal = ref(false);
    const editingId = ref(null);
    const categoryName = ref('');
    const categoryKind = ref('expense');
    const nameError = ref('');
    const saveLoading = ref(false);

    const kindLocked = computed(() =>
      editingId.value ? transactionCount(editingId.value) > 0 : false
    );

    function openAdd() {
      editingId.value = null;
      categoryName.value = '';
      categoryKind.value = 'expense';
      nameError.value = '';
      showModal.value = true;
    }

    function openEdit(category) {
      editingId.value = category.id;
      categoryName.value = category.name || '';
      categoryKind.value = category.kind || 'expense';
      nameError.value = '';
      showModal.value = true;
    }

    function validate() {
      nameError.value = '';
      const name = categoryName.value.trim();
      if (!name) {
        nameError.value = 'Informe o nome da categoria.';
        return false;
      }
      const duplicate = sortedCategories.value.find(
        (c) =>
          c.id !== editingId.value &&
          c.kind === categoryKind.value &&
          (c.name || '').trim().toLowerCase() === name.toLowerCase()
      );
      if (duplicate) {
        nameError.value = `Já existe uma categoria de ${kindLabel(categoryKind.value).toLowerCase()} com este nome.`;
        return false;
      }
      return true;
    }

    async function save() {
      if (!validate()) return;
      saveLoading.value = true;
      try {
        const id = editingId.value || doc(collection(firestore, 'transactionCategories')).id;
        const now = Date.now();
        const existing = editingId.value
          ? categoryStore.transactionCategories[editingId.value]
          : null;
        const payload = {
          name: categoryName.value.trim(),
          kind: kindLocked.value ? (existing?.kind || categoryKind.value) : categoryKind.value,
          isActive: true,
          order: editingId.value
            ? (existing?.order ?? 0)
            : sortedCategories.value.length,
          updatedAt: now,
        };
        if (!editingId.value) payload.createdAt = now;
        await setDoc(doc(firestore, 'transactionCategories', id), payload, { merge: true });
        showModal.value = false;
        showToast(editingId.value ? 'Categoria alterada.' : 'Categoria criada.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar a categoria.', 'error');
      } finally {
        saveLoading.value = false;
      }
    }

    const showDeleteConfirm = ref(false);
    const showReassignModal = ref(false);
    const deleteTarget = ref(null);
    const deleteMessage = ref('');
    const deleteLoading = ref(false);
    const reassignTargetId = ref(null);
    const reassignError = ref('');

    const reassignOptions = computed(() => {
      if (!deleteTarget.value) return [];
      return sortedCategories.value
        .filter((c) => c.kind === deleteTarget.value.kind && c.id !== deleteTarget.value.id)
        .map((c) => ({ id: c.id, label: c.name }));
    });

    function handleDeleteClick(category) {
      deleteTarget.value = category;
      reassignTargetId.value = null;
      reassignError.value = '';
      const count = transactionCount(category.id);
      if (count > 0) {
        const alternatives = reassignOptions.value;
        if (alternatives.length === 0) {
          showToast(
            'Não é possível excluir: é a única categoria deste tipo com transações vinculadas.',
            'error'
          );
          return;
        }
        showReassignModal.value = true;
        return;
      }
      deleteMessage.value = `Tem certeza que deseja excluir "${category.name}"?`;
      showDeleteConfirm.value = true;
    }

    async function doDeleteSimple() {
      if (!deleteTarget.value) return;
      deleteLoading.value = true;
      try {
        await deleteDoc(doc(firestore, 'transactionCategories', deleteTarget.value.id));
        showDeleteConfirm.value = false;
        deleteTarget.value = null;
        showToast('Categoria excluída.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir a categoria.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    }

    async function doDeleteWithReassign() {
      if (!deleteTarget.value) return;
      reassignError.value = '';
      if (!reassignTargetId.value) {
        reassignError.value = 'Selecione a categoria de destino.';
        return;
      }
      deleteLoading.value = true;
      try {
        const categoryId = deleteTarget.value.id;
        const txQuery = query(
          collection(firestore, 'bankTransactions'),
          where('categoryId', '==', categoryId)
        );
        const txSnap = await getDocs(txQuery);
        const docs = txSnap.docs;
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(firestore);
          docs.slice(i, i + 500).forEach((d) => {
            batch.set(d.ref, { categoryId: reassignTargetId.value }, { merge: true });
          });
          await batch.commit();
        }
        await deleteDoc(doc(firestore, 'transactionCategories', categoryId));
        showReassignModal.value = false;
        deleteTarget.value = null;
        showToast('Categoria excluída e transações reatribuídas.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir a categoria.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    }

    return {
      sortedCategories,
      transactionCount,
      kindLabel,
      showModal,
      editingId,
      categoryName,
      categoryKind,
      nameError,
      saveLoading,
      kindLocked,
      openAdd,
      openEdit,
      save,
      showDeleteConfirm,
      showReassignModal,
      deleteTarget,
      deleteMessage,
      deleteLoading,
      reassignTargetId,
      reassignError,
      reassignOptions,
      handleDeleteClick,
      doDeleteSimple,
      doDeleteWithReassign,
    };
  },
};
</script>

<style scoped>
.financeiro-cat-income {
  background: #e8f5e9;
  color: #2e7d32;
}

.financeiro-cat-expense {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.financeiro-cat-usage {
  color: var(--color-text-secondary);
}

.reassign-message {
  margin: 0 0 var(--space-md);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
}

.financeiro-type-toggle {
  display: flex;
  gap: var(--space-xs);
}

.financeiro-toggle-btn {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  padding: 8px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
}

.financeiro-toggle-btn--active {
  background: var(--color-accent-soft);
  color: var(--color-accent);
  font-weight: 600;
  border-color: var(--color-accent);
}

.financeiro-toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger {
  background: var(--color-error);
  border-color: var(--color-error);
}
</style>
